import * as React from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface AppSheetProps {
  /**
   * Controls the open/close state
   */
  open: boolean;
  /**
   * Callback when the sheet is opened or closed
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Sheet title (displayed in header)
   */
  title: string | React.ReactNode;
  /**
   * Optional header actions (e.g., "Clear filters" button)
   */
  headerActions?: React.ReactNode;
  /**
   * Sheet content (form fields, filters, etc.)
   */
  children: React.ReactNode;
  /**
   * Optional footer actions (Cancel, Save, etc.)
   */
  footer?: React.ReactNode;
  /**
   * Custom className for sheet content
   */
  className?: string;
  /**
   * Hide close button (default: false)
   */
  hideClose?: boolean;
  /**
   * Desktop side (default: "right")
   */
  desktopSide?: "left" | "right";
}

/**
 * AppSheet - Unified Mobile Bottom Sheet (Golden Reference)
 * 
 * Based on FabMenu "Quick Actions" popup pattern.
 * 
 * Features:
 * - Mobile: Bottom slide-up with rounded corners and drag handle
 * - Desktop: Side sheet (left or right)
 * - Compact header: Title left, X right, single row
 * - Safe-area aware (iOS notch)
 * - NO autoFocus (keyboard only on manual interaction)
 * - Scrollable body
 * 
 * Usage:
 * Mobile: Bottom sheet with drag handle
 * Desktop: Side sheet (right by default)
 */
export function AppSheet({
  open,
  onOpenChange,
  title,
  headerActions,
  children,
  footer,
  className,
  hideClose = false,
  desktopSide = "right",
}: AppSheetProps) {
  const isMobile = useIsMobile();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : desktopSide}
        className={cn(
          isMobile 
            ? "rounded-t-2xl pb-[env(safe-area-inset-bottom)] max-h-[85dvh]" 
            : "sm:max-w-md overflow-y-auto",
          "flex flex-col p-0",
          className
        )}
        hideClose={true} // We render custom close button
        onOpenAutoFocus={(e) => e.preventDefault()} // Prevent keyboard popup
      >
        {/* Header: Title + Close (single row, compact) */}
        <div className={cn(
          "flex items-center justify-between flex-shrink-0 px-6",
          isMobile ? "pt-2 pb-2" : "py-4 border-b"
        )}>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {typeof title === 'string' ? (
              <h2 className="text-lg font-semibold truncate">{title}</h2>
            ) : (
              title
            )}
            {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
          </div>
          {!hideClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="flex-shrink-0 h-8 w-8 rounded-full"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          )}
        </div>

        {/* Scrollable body */}
        <div className={cn(
          "flex-1 overflow-y-auto overscroll-contain px-6",
          isMobile ? "py-2" : "py-6"
        )}>
          {children}
        </div>

        {/* Footer (if provided) */}
        {footer && (
          <div className={cn(
            "flex-shrink-0 border-t px-6",
            isMobile ? "py-3" : "py-4",
            "flex gap-2",
            isMobile ? "flex-col-reverse" : "justify-end"
          )}>
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * AppSheetFooterButton - Standardized button for AppSheet footer
 */
export function AppSheetFooterButton({
  children,
  variant = "default",
  onClick,
  disabled,
  className,
}: {
  children: React.ReactNode;
  variant?: "default" | "outline" | "destructive";
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  const isMobile = useIsMobile();
  
  return (
    <Button
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        isMobile ? "w-full min-h-[48px]" : "",
        className
      )}
    >
      {children}
    </Button>
  );
}
